<template>
  <div class="agency-profile">
    <v-skeleton-loader
      v-if="loading"
      class="profile-card"
      type="card, list-item, image"
      height="400px"
    />

    <v-card class="profile-card" v-else flat>
      <!-- Header with logo/icon placeholder -->
      <div class="profile-header">
        <v-card-title class="agency-name">
          <span v-text="agency.name" />
        </v-card-title>
      </div>

      <!-- Causes as chips -->
      <v-card-text class="causes-section">
        <site-icon
          :name="icon"
          type="Cause"
          v-for="(icon, key) in agency.causes"
          :key="key"
          :size="40"
          class="mr-3 mb-3"
        />
      </v-card-text>

      <!-- About sections with expandable content -->
      <v-expansion-panels flat multiple class="profile-sections">
        <v-expansion-panel>
          <v-expansion-panel-header class="section-header">
            Who we are
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="section-content" v-text="agency.who_we_are" />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header class="section-header">
            What we do
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="section-content" v-text="agency.what_we_do" />
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Location section -->
        <v-expansion-panel>
          <v-expansion-panel-header class="section-header">
            Location
            <!-- <v-icon  color="#084468">mdi-map-marker</v-icon> -->
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="location-content">
              <div class="address-line">
                <v-icon small class="mr-2" color="#fdbc3a"
                  >mdi-map-marker</v-icon
                >
                <span
                  >{{ agency.address }}, {{ agency.city }}, {{ agency.state }}
                  {{ agency.zip }}</span
                >
              </div>
              <div class="address-line" v-if="agency.address2">
                <v-icon small class="mr-2" color="#fdbc3a"
                  >mdi-map-marker</v-icon
                >
                <span
                  >{{ agency.address2 }}, {{ agency.city }}, {{ agency.state }}
                  {{ agency.zip }}</span
                >
              </div>
              <v-btn small color="#084468" class="mt-3" dark @click="openMap">
                View on Map
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Contacts section -->
        <v-expansion-panel>
          <v-expansion-panel-header class="section-header">
            <!-- <v-icon left color="#084468">mdi-account-box-outline</v-icon> -->
            Contacts
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="contact-item">
              <v-icon small color="#fdbc3a">mdi-phone</v-icon>
              <a :href="`tel:${agency.phone}`" class="contact-link">{{
                formatPhoneNumber(agency.phone)
              }}</a>
            </div>
            <div class="contact-item">
              <v-icon small color="#fdbc3a">mdi-email</v-icon>
              <a :href="`mailto:${agency.email}`" class="contact-link">{{
                agency.email
              }}</a>
            </div>
            <div class="contact-item" v-if="agency.contact_person">
              <v-icon small color="#fdbc3a">mdi-account</v-icon>
              <span>{{ agency.contact_person }}</span>
              <span v-if="agency.contact_title" class="contact-title"
                >({{ agency.contact_title }})</span
              >
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Links section -->
        <v-expansion-panel>
          <v-expansion-panel-header class="section-header">
            <!-- <v-icon left color="#084468">mdi-link-variant</v-icon> -->
            Links
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="social-links">
              <v-btn
                v-if="agency.website"
                :href="agency.website"
                target="_blank"
                small
                color="#084468"
                dark
                class="mb-2"
                block
              >
                <v-icon left>mdi-web</v-icon>
                {{ getHostname(agency.website) }}
              </v-btn>

              <v-btn
                v-if="agency.facebook"
                :href="agency.facebook"
                target="_blank"
                small
                color="#3b5998"
                dark
                class="mb-2"
                block
              >
                <v-icon left>mdi-facebook</v-icon>
                Facebook
              </v-btn>

              <v-btn
                v-if="agency.twitter"
                :href="agency.twitter"
                target="_blank"
                small
                color="#1da1f2"
                dark
                class="mb-2"
                block
              >
                <v-icon left>mdi-twitter</v-icon>
                Twitter
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Photos section -->
        <v-expansion-panel v-if="agency.photos.length > 0">
          <v-expansion-panel-header class="section-header">
            <!-- <v-icon left color="#084468">mdi-image-multiple</v-icon> -->
            Photos
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <agency-photo-viewer :agency_id="agency._id" />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import AgencyPhotoViewer from "@/components/agency/AgencyPhotoViewer.vue";
import SiteIcon from "@/components/common/SiteIcon.vue";

export default {
  components: { AgencyPhotoViewer, SiteIcon },
  data() {
    return {
      loading: true
    };
  },
  computed: {
    ...mapGetters({
      profile: "auth/getProfile",
      profileType: "auth/getType",
      agency: "agency/getAgency"
    })
  },
  watch: {
    agency(val) {
      if (val) {
        this.loading = false;
      }
    }
  },
  methods: {
    getHostname(url) {
      try {
        return new URL(url).hostname.replace("www.", "");
      } catch (e) {
        return url;
      }
    },
    formatPhoneNumber(str) {
      let cleaned = ("" + str).replace(/\D/g, "");
      let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        let intlCode = match[1] ? "+1 " : "";
        return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join(
          ""
        );
      }
      return str;
    },
    openMap() {
      const address = encodeURIComponent(
        `${this.agency.address}, ${this.agency.city}, ${this.agency.state} ${this.agency.zip}`
      );
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${address}`,
        "_blank"
      );
    }
  },
  mounted() {
    if (this.agency) {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.agency-profile {
  max-width: 100%;
  width: 100%;
  padding: 16px;
}

.profile-card {
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  overflow: hidden;
}

.profile-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px 16px;
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
}

.agency-avatar {
  margin-bottom: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.agency-name {
  color: white !important;
  font-weight: 600;
  font-size: 1.5rem;
  text-align: center;
  padding: 0;
  width: 100%;
}

.causes-section {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: 12px !important;
  background-color: #f8f9fa;
}

.profile-sections {
  margin-top: 8px;
}

.section-header {
  font-weight: 600;
  color: #084468 !important;
  min-height: 48px;
}

.section-header .v-icon {
  margin-right: 8px;
}

.section-content {
  padding: 8px 16px;
  line-height: 1.6;
  color: #555;
}

.location-content {
  padding: 8px 16px;
}

.address-line {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #555;
}

.contact-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  color: #555;
}

.contact-item .v-icon {
  margin-right: 12px;
}

.contact-link {
  color: #084468;
  text-decoration: none;
  margin-left: 8px;
}

.contact-link:hover {
  text-decoration: underline;
}

.contact-title {
  margin-left: 8px;
  color: #777;
  font-style: italic;
}

.social-links {
  padding: 8px;
}

/* Responsive adjustments */
@media (min-width: 960px) {
  .agency-profile {
    max-width: 400px;
    padding: 24px 16px;
  }
  .profile-header {
    flex-direction: row;
    justify-content: flex-start;
    padding: 24px;
  }
  .agency-avatar {
    margin-bottom: 0;
    margin-right: 16px;
  }
  .agency-name {
    text-align: left;
  }
}
</style>
